import {Grid, Stack, Typography, Checkbox} from "@mui/material";
import {useTranslation} from "react-i18next";
import {RainDrop} from "./RainDrop";
import dayjs from "dayjs";
import {Frame} from "../../base/Frame";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {useFarmsInsightsItems} from "../../../hooks/fieldInsightsItemsHooks";
import {DataUploadIrrigationRainfallItem} from "./IrrigationRainfallItem";
import {useEffect, useState} from "react";
import {Item} from "../../../types/uploadDataEnums";
import {AppliedIrrigationData, LastAppliedIrrigation} from "../../../types/domain/appliedIrrigation";
import {useParams} from "react-router-dom";
import {useResourceManagement} from "../../../hooks/resourceHooks";
import {getLastAppliedIrrigation} from "../../../services/appliedIrrigationService";
import {swallowException} from "../../../utils/appUtils";
import {CustomDatePicker} from "../../base/CustomDatePicker";
import {CloudUploadIcon} from "../../icons/CloudUploadIcon";

export interface Props {
    setSubmittable: (submit: boolean) => void;
    setFieldsData: (data: AppliedIrrigationData) => void;
    clear: boolean;
    setClear: (value: boolean) => void;
}

export const DataUploadIrrigationRainfallView: React.FC<Props> = ({setSubmittable, setFieldsData, clear, setClear}) => {
    const {t} = useTranslation();
    const {companyId} = useParams();
    const {items} = useFarmsInsightsItems();
    const [fields, setFields] = useState<Item[][]>(items.map(() => []));
    const [initValues, setInitValues] = useState<LastAppliedIrrigation[]>([]);
    const [data, setData] = useState<AppliedIrrigationData>({
        date: dayjs().format("YYYY-MM-DD"),
        fields: [],
        companyId: companyId!,
    });
    const [reprocessNotif, setReprocessNotif] = useState(false);
    const appliedIrrigationManagement = useResourceManagement({
        getLastAppliedIrrigation,
    });

    const updateFields = (index: number, newFields: Item[]) => {
        const newArray = fields.map((arr, i) => (i === index ? newFields : arr));
        setFields(newArray);
    };

    const isPast = (date: string): boolean => {
        return dayjs(date).isBefore(dayjs(), "day");
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        setData((prevData) => {
            let updatedFields = prevData.fields;
            if (fields && fields.length > 0) {
                updatedFields = fields
                    .flat()
                    .filter((item) => !item.error)
                    .map((item) => ({
                        id: item.id!,
                        irrigation: item.irrigation.value,
                        rainfall: item.rainfall.value,
                    }));
            }
            return {
                ...prevData,
                fields: updatedFields,
                reprocessNotif,
            };
        });
    }, [fields, reprocessNotif]);

    useEffect(() => {
        setSubmittable(data.fields.length > 0 && fields.flat().some((field) => !field.error));
        setFieldsData(data);
    }, [data, fields, setSubmittable, setFieldsData]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const parsedDate = dayjs(data.date, "YYYY-MM-DD");
        appliedIrrigationManagement
            .getLastAppliedIrrigation(companyId!, parsedDate)
            .then((res) => setInitValues(res))
            .catch(swallowException);
    }, [data.date]);

    return (
        <Grid item xs={12}>
            <Stack direction="row">
                <RainDrop small={true} />
                <Typography fontSize={16} textTransform="uppercase" sx={{mb: "1rem", ml: "0.75rem"}}>
                    {t("dialogs.data_upload.irrigation_rainfall.type")}
                </Typography>
            </Stack>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Frame>
                        <Stack direction="row">
                            <Grid item xs={4} sx={{mr: "1.25rem"}}>
                                <CustomDatePicker
                                    date={data.date ? dayjs(data.date, "YYYY-MM-DD") : null}
                                    onDateChange={(newValue) => {
                                        if (newValue) {
                                            const onlyDate = newValue.format("YYYY-MM-DD");
                                            setData({...data, date: onlyDate});
                                        }
                                    }}
                                    disabled={false}
                                    maxDate={dayjs()}
                                />
                            </Grid>
                            <Grid item xs={8}>
                                {data?.date && isPast(data.date) && (
                                    <Stack direction="row" alignItems="flex-start" spacing={2}>
                                        <Stack direction="column" alignItems="center" spacing={1}>
                                            <InfoOutlinedIcon fontSize="small" color="primary" />
                                            <Checkbox
                                                checked={reprocessNotif}
                                                onChange={(e) => setReprocessNotif(e.target.checked)}
                                            />
                                        </Stack>

                                        <Stack direction="column" spacing={1} justifyContent="center">
                                            <Typography fontSize={14}>{t("dialogs.data_upload.info")}</Typography>
                                            <Typography fontSize={14}>
                                                {t("dialogs.data_upload.notify_reprocessing")}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                )}
                            </Grid>
                        </Stack>
                    </Frame>
                </Grid>
                <Grid item xs={12}>
                    <Frame sx={{padding: 0}}>
                        <Stack direction="row" sx={{padding: "6px 10px 3px 10px", backgroundColor: "#F3F5FA"}}>
                            <Grid
                                item
                                xs={7}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Typography fontSize={14} fontWeight={500} textTransform="uppercase">
                                    {t("dialogs.data_upload.irrigation_rainfall.farm_field")}
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography fontSize={14} fontWeight={500} textTransform="uppercase">
                                    {t("dialogs.data_upload.irrigation_rainfall.irrigation")}
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography fontSize={14} fontWeight={500} textTransform="uppercase">
                                    {t("dialogs.data_upload.irrigation_rainfall.rainfall")}
                                </Typography>
                            </Grid>
                            <Grid item xs={1} sx={{textAlign: "right"}}>
                                <CloudUploadIcon sx={{stroke: "#737990", width: "1.25rem", height: "1.25rem"}} />
                            </Grid>
                        </Stack>

                        {items.map((item, i) => (
                            <DataUploadIrrigationRainfallItem
                                key={i}
                                farm={item.farm}
                                fields={item.fields}
                                setFields={(items) => updateFields(i, items)}
                                initValues={initValues}
                                clear={clear}
                                setClear={setClear}
                            />
                        ))}
                    </Frame>
                </Grid>
            </Grid>
        </Grid>
    );
};
